/* eslint-disable @typescript-eslint/naming-convention */
import { mapKeys, mapValues } from 'lodash';

import type { BrandLogo, SiteHeader } from '@swf/cms';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  ExtractDispatchFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { Menu } from '@swf/core/types/CMS';

interface HeaderResponse {
  primary: Menu;
  secondary: Menu;
  main?: Menu;
  brandIcon?: BrandLogo[];
  brandIconUrl?: string;
  layout: string;
}

interface HeaderState extends SiteHeader {
  cacheControl?: string;
}

const reducers = {
  preloaded: (state: HeaderState, payload: HeaderState) => payload
};

const keyMap = {
  href: 'url',
  children: 'title',
  type: 'icon',
  resolver_tag: 'resolver_tag',
  show_on_url: 'show_on_url'
};

const effects = {
  async preload(
    payload: undefined,
    { request, dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HeaderWidgetModel> = dispatch[id];

    const { body: data, headers } = await Promise.resolve(
      request<HeaderResponse>({ service: 'cms.header' })
    );

    await Promise.all(
      [...data.secondary, ...(data.main || []), ...data.primary]
        .filter(
          (item) => item.resolver_tag && !select.resolver.getTag(rootState, item.resolver_tag)
        )
        .map(({ resolver_tag }) => dispatch.resolver.fetchTag({ tag: resolver_tag as string }))
    );

    if (data) {
      const {
        main: main_navigation = [],
        primary: support_navigation,
        secondary: secondary_navigation,
        brandIcon: brand_icon = [],
        brandIconUrl: brand_icon_url = ''
      } = mapValues(data, (value, key) =>
        value && ['primary', 'secondary', 'main'].includes(key)
          ? (value as Menu).map((entry) =>
              mapKeys(entry, (_, key) => keyMap[key as keyof typeof keyMap])
            )
          : value
      );

      widgetDispatch.preloaded({
        main_navigation,
        support_navigation,
        secondary_navigation,
        brand_icon,
        brand_icon_url,
        layout: data.layout,
        cacheControl: headers.cacheControl
      } as unknown as HeaderState);
    }
  }
};

const selectors = (slice: Slicer<HeaderState>) => ({
  get() {
    return slice;
  }
});

export interface HeaderWidgetModel extends WidgetModelConfig<void, HeaderState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const headerWidgetModel: HeaderWidgetModel = {
  state: {
    support_navigation: [],
    main_navigation: [],
    secondary_navigation: [],
    brand_icon: [],
    brand_icon_url: '',
    layout: 'Real Money'
  } as unknown as HeaderState,
  reducers,
  effects,
  selectors
};

export const useHeaderWidget: UseWidget<void, HeaderWidgetModel> = useWidget;
